import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Redirect, useLocation } from "react-router-dom";
import { RootState } from "typedefs";
import { Loading } from "app/components/Wrappers";
import { fetchSelf } from "app/actions/user";
import { useActions } from "app/utils/hooks";
import { isAcademyOnboardingCoach } from "app/utils/subscription";

interface AcademyWizardGuardProps {
  children: React.ReactNode;
}

/**
 * The wizard is for the coach who owns the Academy account (see
 * isAcademyOnboardingCoach). Rendered inside Authenticated, so the
 * token check comes first. While the account is still loading (a hard
 * refresh) it shows a spinner rather than judging a null user; anyone else is
 * sent home.
 *
 * The store gets one forced refresh, then the verdict, in two cases where it
 * cannot be trusted as it stands: arriving from checkout (router state
 * carries the entry point) the plan may still predate the purchase if the
 * post-checkout refresh failed; and a boot fetch that failed with a valid
 * token leaves no account at all — the router fetches once and nothing
 * retries, so without this the spinner would never end. If the refresh
 * fails too, the coach goes home, where the app's own handling applies.
 */
const AcademyWizardGuard = ({ children }: AcademyWizardGuardProps) => {
  const { self, pending, error } = useSelector(
    (state: RootState) => ({
      self: state.user.self,
      pending: state.user.pending,
      error: state.user.error,
    }),
    shallowEqual,
  );
  const location = useLocation<{ wizardEntryPoint?: string } | undefined>();
  const actions = useActions({ fetchSelf });
  const fromCheckout = Boolean(location.state?.wizardEntryPoint);
  const eligible = isAcademyOnboardingCoach(self);
  // No account and the request is over with an error: the fetch failed
  // (before the first dispatch, and while it runs, `error` is null).
  const fetchFailed = !self && !pending && error !== null;
  // "idle" → "refreshing" → "done"; only ever one attempt per mount.
  const [refresh, setRefresh] = useState<"idle" | "refreshing" | "done">(
    "idle",
  );

  useEffect(() => {
    const needsRefresh = self ? !eligible && fromCheckout : fetchFailed;
    if (!needsRefresh || refresh !== "idle") {
      return;
    }
    setRefresh("refreshing");
    Promise.resolve(actions.fetchSelf(true))
      .catch((refreshError) => {
        console.error("Error refreshing account for the wizard:", refreshError);
      })
      .then(() => setRefresh("done"));
  }, [self, eligible, fromCheckout, fetchFailed, refresh, actions]);

  if (!self) {
    if (fetchFailed && refresh === "done") {
      return <Redirect to="/" />;
    }
    return <Loading isLoading loadType="spinner" />;
  }
  if (!eligible && fromCheckout && refresh !== "done") {
    return <Loading isLoading loadType="spinner" />;
  }
  if (!eligible) {
    return <Redirect to="/" />;
  }
  return <>{children}</>;
};

export default AcademyWizardGuard;
